import React from 'react'
import Header from '../header'
import { ActionButton } from '@emerald-works-nova/components'
import {
  FiltersContainer as Filters,
  SearchFilter
} from '../../../../components/filters'
import UserUploadLoading from './upload-loading'
import PermissionsUploadLoading from './upload-permissions-loading'
import BulkDeleteLoading from '../user-delete/bulk-delete-loading'
import UserEmailUpdateLoading from './user-email-update-loading'
import UserList from './list'
import UserDropdown from './user-dropdown'
import { Grid, Typography, Select, MenuItem } from '@mui/material'
import { useSelector } from 'react-redux'
import { TenantEditSlice, UserListSlice, UserUploadSlice, PermissionsUploadSlice, UserDeleteSlice, UserEmailUpdateSlice } from '../../../../reducers'
import UserUploadResults from './upload-results'
import PermissionsUploadResults from './upload-permissions-results'
import UserMultipleDeleteResults from './delete-results'
import UserEmailUpdateResults from './user-email-update-results'
import { useArkMode } from '../../../../hooks/useArkMode'
import CopyToClipboard from 'react-copy-to-clipboard'
import { BannerStatic } from '../../../../components'
import { useTenantFormContext } from '../../tenant-form-context'
import { buildToolkitUrl } from '../../../../helpers/toolkit'
import { ContentCopy } from '@mui/icons-material'

const UserListView = ({ onClickUploadUsers, onClickDeleteUsers, onClickUploadPermissions, onClickNameIdStart }) => {
  const isUploadingUsers = useSelector(UserUploadSlice.selectors.selectLoading)
  const isUploadingPermissions = useSelector(PermissionsUploadSlice.selectors.selectLoading)
  const isDeletingUsers = useSelector(UserDeleteSlice.selectors.selectLoading)
  const isUpdatingEmail = useSelector(UserEmailUpdateSlice.selectors.selectLoading)
  const isUploading = isUploadingUsers || isUploadingPermissions || isUpdatingEmail || isDeletingUsers
  const tenant = useSelector(TenantEditSlice.selectors.tenant)
  const total = useSelector(UserListSlice.selectors.selectTotal)
  const [filters, setFilters] = React.useState({ search: '', field: 'email' })

  const { isAdminMode } = useArkMode()

  const { envInfo } = useTenantFormContext()

  const registrationUrl = React.useMemo(() => {
    if (!envInfo || !tenant) return null

    const isSelfRegistrationAvailable = tenant?.loginProviders?.some((lp) => lp.providerType === 'USERNAME' && lp.selfRegistration)

    if (!isSelfRegistrationAvailable) return null
    const { stage, tld } = envInfo
    return buildToolkitUrl(tenant.tenantKey, stage, tld) + '/signup'
  }, [tenant, envInfo])

  const title = React.useMemo(() => {
    let userCount = total || null

    if (tenant.maxNumberOfUsers && total) {
      userCount = `${total}/${tenant.maxNumberOfUsers} max`
    }

    return (
      <Typography variant='h6' noWrap>Users <Typography component='span' variant='body1' data-test='max-users'>{userCount}</Typography></Typography>
    )
  }, [tenant.maxNumberOfUsers, total])

  const handleCopy = () => BannerStatic.show({ label: 'Registration link copied to clipboard' })

  return (
    <>
      <Header
        title={title}
        actions={
          <>
            <Grid
              item
              aria-label='List filters'
              tabIndex={0}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <Grid container spacing={1} alignItems='center'>
                <Grid item>
                  <Select
                    value={filters.field}
                    onChange={e => setFilters({ search: '', field: e.target.value })}
                    size='small'
                    data-test='search-field-select'
                  >
                    <MenuItem value='email'>Email</MenuItem>
                    <MenuItem value='username'>Username</MenuItem>
                    <MenuItem value='firstname'>First Name</MenuItem>
                    <MenuItem value='lastname'>Last Name</MenuItem>
                  </Select>
                </Grid>
                <Grid item xs>
                  <Filters filters={filters} onChange={setFilters}>
                    <SearchFilter
                      name='search'
                      label={`Search (${filters.field})`}
                      isLoading={isUploading}
                    />
                  </Filters>
                </Grid>
                {isAdminMode && (
                  <Grid item sx={{ ml: -2 }}>
                    <UserDropdown
                      onClickUploadUsers={onClickUploadUsers}
                      onClickDeleteUsers={onClickDeleteUsers}
                      onClickUploadPermissions={onClickUploadPermissions}
                      onClickNameIdStart={onClickNameIdStart}
                      isUploading={isUploading}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            {!isAdminMode && registrationUrl && (
              <Grid item>
                <CopyToClipboard text={registrationUrl} onCopy={handleCopy}>
                  <ActionButton
                    variant='primary'
                    dataTest='copy-registration-link'
                    endIcon={<ContentCopy />}
                  >
                    Copy Registration Link
                  </ActionButton>
                </CopyToClipboard>
              </Grid>
            )}
          </>
        }
        disabled={isUploading}
      />
      <PermissionsUploadResults />
      <UserUploadResults />
      <UserEmailUpdateResults />
      <UserMultipleDeleteResults />
      {isUploadingUsers && <UserUploadLoading />}
      {isUploadingPermissions && <PermissionsUploadLoading />}
      {isDeletingUsers && <BulkDeleteLoading />}
      {isUpdatingEmail && <UserEmailUpdateLoading />}
      {!isUploading && <UserList filters={filters} />}
    </>
  )
}

export default UserListView
